import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils'

export const MODEL_PARTS_ANALYSIS_VERSION = 8

const getAttributeComponent = (attribute, index, component) => {
    if (!attribute) return 0

    if (typeof attribute.getComponent === 'function') {
        return attribute.getComponent(index, component)
    }

    if (component === 0 && typeof attribute.getX === 'function') return attribute.getX(index)
    if (component === 1 && typeof attribute.getY === 'function') return attribute.getY(index)
    if (component === 2 && typeof attribute.getZ === 'function') return attribute.getZ(index)
    if (component === 3 && typeof attribute.getW === 'function') return attribute.getW(index)

    const array = attribute?.isInterleavedBufferAttribute
        ? attribute.data?.array
        : attribute?.array
    const stride = attribute?.isInterleavedBufferAttribute
        ? attribute.data?.stride
        : attribute?.itemSize
    const offset = attribute?.isInterleavedBufferAttribute
        ? attribute.offset
        : 0

    return Number(array?.[index * stride + offset + component] || 0)
}

const copyAttributeByVertexMap = (attribute, sourceVertexIds) => {
    const ArrayType = attribute?.array?.constructor ||
        attribute?.data?.array?.constructor ||
        Float32Array
    const itemSize = Number(attribute?.itemSize || 1)
    const result = new ArrayType(sourceVertexIds.length * itemSize)

    sourceVertexIds.forEach((sourceIndex, targetIndex) => {
        for (let component = 0; component < itemSize; component += 1) {
            result[targetIndex * itemSize + component] = getAttributeComponent(
                attribute,
                sourceIndex,
                component
            )
        }
    })

    return new THREE.BufferAttribute(
        result,
        itemSize,
        !!attribute?.normalized
    )
}

const removeUnsupportedGltfColorSets = (gltfJson) => {
    let changed = false

    ;(Array.isArray(gltfJson?.meshes) ? gltfJson.meshes : []).forEach((mesh) => {
        ;(Array.isArray(mesh?.primitives) ? mesh.primitives : []).forEach((primitive) => {
            const attributes = primitive?.attributes || null
            const dracoAttributes = primitive?.extensions?.KHR_draco_mesh_compression?.attributes || null

            ;['COLOR_1', 'COLOR_2'].forEach((semantic) => {
                if (attributes && Object.prototype.hasOwnProperty.call(attributes, semantic)) {
                    delete attributes[semantic]
                    changed = true
                }

                if (dracoAttributes && Object.prototype.hasOwnProperty.call(dracoAttributes, semantic)) {
                    delete dracoAttributes[semantic]
                    changed = true
                }
            })
        })
    })

    return changed
}

export const prepareGltfSourceForRuntime = (source, fileType) => {
    if (fileType === 'gltf' && typeof source === 'string') {
        try {
            const json = JSON.parse(source)

            if (removeUnsupportedGltfColorSets(json)) {
                return JSON.stringify(json)
            }
        } catch (_error) { }

        return source
    }

    if (fileType !== 'glb' || !(source instanceof ArrayBuffer)) return source

    try {
        const sourceBytes = new Uint8Array(source)
        const view = new DataView(source)
        const magic = view.getUint32(0, true)
        const version = view.getUint32(4, true)

        if (magic !== 0x46546c67 || version !== 2) return source

        let offset = 12

        while (offset + 8 <= sourceBytes.byteLength) {
            const chunkLength = view.getUint32(offset, true)
            const chunkType = view.getUint32(offset + 4, true)
            const chunkStart = offset + 8
            const chunkEnd = chunkStart + chunkLength

            if (chunkEnd > sourceBytes.byteLength) return source

            if (chunkType === 0x4e4f534a) {
                const jsonText = new TextDecoder().decode(
                    sourceBytes.subarray(chunkStart, chunkEnd)
                ).replace(/[\u0000\s]+$/g, '')
                const json = JSON.parse(jsonText)

                if (!removeUnsupportedGltfColorSets(json)) return source

                const encoded = new TextEncoder().encode(JSON.stringify(json))
                if (encoded.byteLength > chunkLength) return source

                const result = new Uint8Array(source.slice(0))
                result.fill(0x20, chunkStart, chunkEnd)
                result.set(encoded, chunkStart)

                return result.buffer
            }

            offset = chunkEnd
        }
    } catch (_error) { }

    return source
}

const stripUnusedGltfAttributes = (geometry) => {
    if (!geometry?.attributes) return geometry

    Object.keys(geometry.attributes).forEach((name) => {
        const normalized = String(name || '').toLowerCase()
        const isUnsupportedExtraColor = (
            normalized === 'color_1' ||
            normalized === 'color_2' ||
            normalized === '_color_1' ||
            normalized === '_color_2'
        )

        if (isUnsupportedExtraColor) {
            geometry.deleteAttribute(name)
        }
    })

    return geometry
}

export const ensureIndexedGeometry = (geometry) => {
    if (!geometry?.isBufferGeometry) return geometry
    if (geometry.index) return geometry

    const vertexCount = Number(geometry.getAttribute('position')?.count || 0)
    const IndexArray = vertexCount > 65535 ? Uint32Array : Uint16Array
    const index = new IndexArray(vertexCount)

    for (let i = 0; i < vertexCount; i += 1) index[i] = i

    geometry.setIndex(new THREE.BufferAttribute(index, 1))

    return geometry
}

const buildIndexedGeometryFromGroup = (sourceGeometry, group) => {
    const sourceIndex = sourceGeometry.index

    if (!sourceIndex) {
        const geometry = new THREE.BufferGeometry()
        const startVertex = Math.max(0, Number(group.start || 0))
        const vertexCount = Math.max(0, Number(group.count || 0))
        const sourceVertexIds = new Array(vertexCount)

        for (let i = 0; i < vertexCount; i += 1) {
            sourceVertexIds[i] = startVertex + i
        }

        Object.entries(sourceGeometry.attributes || {}).forEach(([name, attribute]) => {
            geometry.setAttribute(name, copyAttributeByVertexMap(attribute, sourceVertexIds))
        })

        return ensureIndexedGeometry(geometry)
    }

    const start = Math.max(0, Number(group.start || 0))
    const count = Math.max(0, Math.min(
        Number(group.count || 0),
        Number(sourceIndex.count || 0) - start
    ))
    const oldToNew = new Map()
    const sourceVertexIds = []
    const IndexArray = count > 65535 ? Uint32Array : Uint16Array
    const nextIndex = new IndexArray(count)

    for (let i = 0; i < count; i += 1) {
        const sourceVertexIndex = Number(sourceIndex.getX(start + i))
        let targetVertexIndex = oldToNew.get(sourceVertexIndex)

        if (targetVertexIndex === undefined) {
            targetVertexIndex = sourceVertexIds.length
            oldToNew.set(sourceVertexIndex, targetVertexIndex)
            sourceVertexIds.push(sourceVertexIndex)
        }

        nextIndex[i] = targetVertexIndex
    }

    const geometry = new THREE.BufferGeometry()

    Object.entries(sourceGeometry.attributes || {}).forEach(([name, attribute]) => {
        geometry.setAttribute(name, copyAttributeByVertexMap(attribute, sourceVertexIds))
    })

    geometry.setIndex(new THREE.BufferAttribute(nextIndex, 1))

    return geometry
}

const countSourceMeshesAndPrimitives = (root) => {
    let sourceMeshCount = 0
    let sourcePrimitiveCount = 0

    root.traverse((child) => {
        if (!child?.isMesh || !child.geometry) return

        sourceMeshCount += 1
        const groups = Array.isArray(child.geometry.groups) && child.geometry.groups.length
            ? child.geometry.groups
            : [null]

        sourcePrimitiveCount += groups.length
    })

    return {
        sourceMeshCount,
        sourcePrimitiveCount,
    }
}

export const collectMergedGeometryAndMaterials = (root, {
    onMesh,
    onGroup,
} = {}) => {
    const geometries = []
    const materials = []
    const materialIndexByKey = new Map()
    const geometryMaterialIndices = []
    const sourceStats = countSourceMeshesAndPrimitives(root)

    root.updateMatrixWorld(true)

    root.traverse((child) => {
        if (!child?.isMesh || !child.geometry) return

        child.updateWorldMatrix(true, false)

        const baseGeometry = stripUnusedGltfAttributes(child.geometry.clone())
        baseGeometry.applyMatrix4(child.matrixWorld)

        if (!baseGeometry.getAttribute('normal') && baseGeometry.getAttribute('position')) {
            baseGeometry.computeVertexNormals()
        }

        ensureIndexedGeometry(baseGeometry)

        const localMaterials = Array.isArray(child.material)
            ? child.material
            : [child.material || new THREE.MeshStandardMaterial({ color: 0x808080 })]
        const groups = Array.isArray(baseGeometry.groups) && baseGeometry.groups.length
            ? [...baseGeometry.groups]
            : [{
                start: 0,
                count: baseGeometry.index
                    ? baseGeometry.index.count
                    : baseGeometry.getAttribute('position').count,
                materialIndex: 0,
            }]
        const canKeepWholeGeometry = groups.length === 1

        onMesh?.({ child, baseGeometry, localMaterials, groups })

        groups.forEach((group, groupIndex) => {
            const localMaterial = localMaterials[group.materialIndex] ||
                localMaterials[0] ||
                new THREE.MeshStandardMaterial({ color: 0x808080 })
            const key = localMaterial.uuid || localMaterial.name || `material-${materials.length}`

            if (!materialIndexByKey.has(key)) {
                materialIndexByKey.set(key, materials.length)
                materials.push(localMaterial)
            }

            const globalMaterialIndex = materialIndexByKey.get(key)
            const geometry = canKeepWholeGeometry && groupIndex === 0
                ? baseGeometry
                : buildIndexedGeometryFromGroup(baseGeometry, group)
            const count = geometry.index
                ? geometry.index.count
                : geometry.getAttribute('position').count

            geometry.clearGroups()
            geometry.addGroup(0, count, globalMaterialIndex)
            geometries.push(geometry)
            geometryMaterialIndices.push(globalMaterialIndex)

            onGroup?.({
                child,
                group,
                groupIndex,
                localMaterial,
                globalMaterialIndex,
                geometry,
                count,
            })
        })

        if (!canKeepWholeGeometry) {
            baseGeometry.dispose()
        }
    })

    if (!geometries.length) {
        throw new Error('В 3D-файле не найдена геометрия')
    }

    const geometry = mergeGeometries(geometries, true)

    if (!geometry) {
        geometries.forEach((item) => item?.dispose?.())
        throw new Error('Не удалось объединить геометрию 3D-модели')
    }

    geometries.forEach((item) => item?.dispose?.())

    geometry.groups.forEach((group, index) => {
        group.materialIndex = geometryMaterialIndices[index] ?? group.materialIndex ?? 0
    })

    if (!geometry.getAttribute('normal') && geometry.getAttribute('position')) {
        geometry.computeVertexNormals()
    }

    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()

    return {
        geometry,
        materials,
        ...sourceStats,
    }
}

const createUnionFind = (size) => {
    const parent = new Int32Array(size)
    const rank = new Uint8Array(size)

    for (let index = 0; index < size; index += 1) parent[index] = index

    const find = (value) => {
        let current = value

        while (parent[current] !== current) {
            parent[current] = parent[parent[current]]
            current = parent[current]
        }

        return current
    }

    const union = (left, right) => {
        let rootLeft = find(left)
        let rootRight = find(right)

        if (rootLeft === rootRight) return false

        if (rank[rootLeft] < rank[rootRight]) {
            const swap = rootLeft
            rootLeft = rootRight
            rootRight = swap
        }

        parent[rootRight] = rootLeft

        if (rank[rootLeft] === rank[rootRight]) {
            rank[rootLeft] += 1
        }

        return true
    }

    return { find, union }
}

const hashText = (value = '') => {
    let hash = 2166136261
    const text = String(value || '')

    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index)
        hash = Math.imul(hash, 16777619)
    }

    return (hash >>> 0).toString(36)
}

const sampleSortedValues = (values = [], maximumCount = 96) => {
    const sorted = [...values].sort((left, right) => left - right)
    const count = Math.min(
        Math.max(1, Number(maximumCount) || 96),
        sorted.length
    )
    const sample = []

    for (let index = 0; index < count; index += 1) {
        const sourceIndex = count <= 1
            ? 0
            : Math.round((index / (count - 1)) * (sorted.length - 1))

        sample.push(Number(sorted[sourceIndex]) || 0)
    }

    return sample
}

const quantizeShapeSample = (values = [], multiplier = 2000) => (
    values.map((value) => Math.round((Number(value) || 0) * multiplier))
)

const buildPartShapeDescriptor = (geometry, dimensions, triangleCount) => {
    const position = geometry?.getAttribute?.('position') || null
    const index = geometry?.index || null
    const sourceDimensions = [
        Math.max(0, Number(dimensions?.x) || 0),
        Math.max(0, Number(dimensions?.y) || 0),
        Math.max(0, Number(dimensions?.z) || 0),
    ]
    const sortedDimensions = [...sourceDimensions].sort((left, right) => left - right)
    const longestSide = Math.max(0.000001, ...sourceDimensions)
    const radiusValues = []
    const edgeValues = []
    const areaValues = []

    for (let vertexIndex = 0; vertexIndex < Number(position?.count || 0); vertexIndex += 1) {
        const x = Number(position.getX(vertexIndex)) || 0
        const y = Number(position.getY(vertexIndex)) || 0
        const z = Number(position.getZ(vertexIndex)) || 0
        const normalizedRadius = Math.sqrt((x * x) + (y * y) + (z * z)) / longestSide

        radiusValues.push(normalizedRadius)
    }

    if (position && index) {
        const a = new THREE.Vector3()
        const b = new THREE.Vector3()
        const c = new THREE.Vector3()
        const ab = new THREE.Vector3()
        const ac = new THREE.Vector3()
        const normalizedAreaScale = longestSide * longestSide
        const safeTriangleCount = Math.min(
            Math.max(0, Number(triangleCount) || 0),
            Math.floor(Number(index.count || 0) / 3)
        )

        for (let triangleIndex = 0; triangleIndex < safeTriangleCount; triangleIndex += 1) {
            const offset = triangleIndex * 3
            const ia = Number(index.getX(offset))
            const ib = Number(index.getX(offset + 1))
            const ic = Number(index.getX(offset + 2))

            a.set(
                Number(position.getX(ia)) || 0,
                Number(position.getY(ia)) || 0,
                Number(position.getZ(ia)) || 0
            )
            b.set(
                Number(position.getX(ib)) || 0,
                Number(position.getY(ib)) || 0,
                Number(position.getZ(ib)) || 0
            )
            c.set(
                Number(position.getX(ic)) || 0,
                Number(position.getY(ic)) || 0,
                Number(position.getZ(ic)) || 0
            )

            edgeValues.push(
                a.distanceTo(b) / longestSide,
                b.distanceTo(c) / longestSide,
                c.distanceTo(a) / longestSide
            )

            ab.copy(b).sub(a)
            ac.copy(c).sub(a)
            areaValues.push(
                ab.cross(ac).length() * 0.5 / normalizedAreaScale
            )
        }
    }

    const radiusSample = sampleSortedValues(radiusValues, 96)
    const edgeSample = sampleSortedValues(edgeValues, 128)
    const areaSample = sampleSortedValues(areaValues, 96)
    const dimensionKey = sortedDimensions
        // 0.5 мм: зеркальные копии после Draco/Float32 не разделяются
        // из-за шума в последних знаках, но разные реальные заготовки остаются разными.
        .map((value) => Math.round(value * 2000))
        .join('x')
    const fingerprintText = [
        dimensionKey,
        Math.max(0, Number(triangleCount) || 0),
        Math.max(0, Number(position?.count) || 0),
        quantizeShapeSample(radiusSample, 2000).join(','),
        quantizeShapeSample(edgeSample, 2000).join(','),
        quantizeShapeSample(areaSample, 4000).join(','),
    ].join('|')

    return {
        signature: `${dimensionKey}:${triangleCount}:${hashText(fingerprintText)}`,
        profile: {
            sortedDimensions,
            longestSide,
            triangleCount: Math.max(0, Number(triangleCount) || 0),
            vertexCount: Math.max(0, Number(position?.count) || 0),
            radiusSample,
            edgeSample,
            areaSample,
        },
    }
}

const compareShapeSamples = (left = [], right = [], {
    averageTolerance,
    maximumTolerance,
}) => {
    if (left.length !== right.length || !left.length) return left.length === right.length

    let sum = 0
    let maximum = 0

    for (let index = 0; index < left.length; index += 1) {
        const difference = Math.abs((Number(left[index]) || 0) - (Number(right[index]) || 0))
        sum += difference
        maximum = Math.max(maximum, difference)
    }

    return (
        maximum <= maximumTolerance &&
        sum / left.length <= averageTolerance
    )
}

const arePartShapeProfilesEquivalent = (left = null, right = null) => {
    if (!left || !right) return false
    if (left.triangleCount !== right.triangleCount) return false
    if (left.vertexCount !== right.vertexCount) return false

    const dimensionTolerance = Math.max(
        0.00075,
        Math.max(left.longestSide, right.longestSide) * 0.001
    )

    for (let index = 0; index < 3; index += 1) {
        if (
            Math.abs(
                (Number(left.sortedDimensions?.[index]) || 0) -
                (Number(right.sortedDimensions?.[index]) || 0)
            ) > dimensionTolerance
        ) {
            return false
        }
    }

    return (
        compareShapeSamples(left.radiusSample, right.radiusSample, {
            averageTolerance: 0.0015,
            maximumTolerance: 0.008,
        }) &&
        compareShapeSamples(left.edgeSample, right.edgeSample, {
            averageTolerance: 0.0015,
            maximumTolerance: 0.008,
        }) &&
        compareShapeSamples(left.areaSample, right.areaSample, {
            averageTolerance: 0.002,
            maximumTolerance: 0.012,
        })
    )
}

const canonicalizeEquivalentPartSignatures = (parts = []) => {
    const representatives = []

    ;(Array.isArray(parts) ? parts : []).forEach((part) => {
        const profile = part?.shapeProfile || null
        let representative = null

        for (let index = 0; index < representatives.length; index += 1) {
            const candidate = representatives[index]

            if (!arePartShapeProfilesEquivalent(profile, candidate.shapeProfile)) continue

            representative = candidate
            break
        }

        if (!representative) {
            representatives.push(part)
            return
        }

        part.signature = representative.signature
        part.geometry.userData = {
            ...(part.geometry.userData || {}),
            __modelPartSignature: representative.signature,
        }
    })
}

const assignStablePartTypeOrder = (parts = []) => {
    canonicalizeEquivalentPartSignatures(parts)

    const groupsBySignature = new Map()

    ;(Array.isArray(parts) ? parts : []).forEach((part) => {
        const signature = String(part?.signature || part?.key || '')
        if (!signature) return

        if (!groupsBySignature.has(signature)) {
            const dimensions = part?.dimensions || {}
            const x = Math.max(0, Number(dimensions.x) || 0)
            const y = Math.max(0, Number(dimensions.y) || 0)
            const z = Math.max(0, Number(dimensions.z) || 0)

            groupsBySignature.set(signature, {
                signature,
                parts: [],
                sizeSum: x + y + z,
                volume: x * y * z,
                longestSide: Math.max(x, y, z),
                triangleCount: Math.max(0, Number(part?.triangleCount) || 0),
            })
        }

        groupsBySignature.get(signature).parts.push(part)
    })

    const orderedGroups = [...groupsBySignature.values()].sort((left, right) => (
        (right.sizeSum - left.sizeSum) ||
        (right.volume - left.volume) ||
        (right.longestSide - left.longestSide) ||
        (right.triangleCount - left.triangleCount) ||
        left.signature.localeCompare(right.signature)
    ))

    orderedGroups.forEach((group, typeIndex) => {
        group.parts.sort((left, right) => (
            (Number(left?.index) - Number(right?.index)) ||
            ((Number(left?.center?.[2]) || 0) - (Number(right?.center?.[2]) || 0)) ||
            ((Number(left?.center?.[0]) || 0) - (Number(right?.center?.[0]) || 0)) ||
            ((Number(left?.center?.[1]) || 0) - (Number(right?.center?.[1]) || 0))
        ))

        group.parts.forEach((part, occurrenceIndex) => {
            part.typeIndex = typeIndex
            part.occurrenceIndex = occurrenceIndex
            part.quantity = group.parts.length
        })
    })

    return orderedGroups
}

const getTriangleMaterialIndices = (geometry, triangleCount) => {
    const result = new Int32Array(triangleCount)
    const groups = Array.isArray(geometry?.groups) && geometry.groups.length
        ? geometry.groups
        : [{ start: 0, count: triangleCount * 3, materialIndex: 0 }]

    groups.forEach((group) => {
        const startTriangle = Math.max(0, Math.floor((Number(group.start) || 0) / 3))
        const endTriangle = Math.min(
            triangleCount,
            Math.ceil(((Number(group.start) || 0) + (Number(group.count) || 0)) / 3)
        )
        const materialIndex = Math.max(0, Number(group.materialIndex) || 0)

        for (let triangleIndex = startTriangle; triangleIndex < endTriangle; triangleIndex += 1) {
            result[triangleIndex] = materialIndex
        }
    })

    return result
}

const buildDisconnectedPartGeometry = ({
    sourceGeometry,
    sourceIndex,
    triangleIds,
    triangleMaterialIndices,
}) => {
    const trianglesByMaterial = new Map()

    triangleIds.forEach((triangleId) => {
        const materialIndex = triangleMaterialIndices[triangleId] || 0

        if (!trianglesByMaterial.has(materialIndex)) {
            trianglesByMaterial.set(materialIndex, [])
        }

        trianglesByMaterial.get(materialIndex).push(triangleId)
    })

    const sourceVertexIds = []
    const oldToNew = new Map()
    const targetIndices = []
    const groups = []

    const getTargetIndex = (sourceVertexIndex) => {
        let targetIndex = oldToNew.get(sourceVertexIndex)

        if (targetIndex !== undefined) return targetIndex

        targetIndex = sourceVertexIds.length
        oldToNew.set(sourceVertexIndex, targetIndex)
        sourceVertexIds.push(sourceVertexIndex)

        return targetIndex
    }

    ;[...trianglesByMaterial.entries()]
        .sort(([left], [right]) => left - right)
        .forEach(([materialIndex, materialTriangles]) => {
            const start = targetIndices.length

            materialTriangles.sort((left, right) => left - right).forEach((triangleId) => {
                const offset = triangleId * 3

                targetIndices.push(
                    getTargetIndex(Number(sourceIndex.getX(offset))),
                    getTargetIndex(Number(sourceIndex.getX(offset + 1))),
                    getTargetIndex(Number(sourceIndex.getX(offset + 2)))
                )
            })

            groups.push({
                start,
                count: targetIndices.length - start,
                materialIndex,
            })
        })

    const geometry = new THREE.BufferGeometry()

    Object.entries(sourceGeometry.attributes || {}).forEach(([name, attribute]) => {
        geometry.setAttribute(name, copyAttributeByVertexMap(attribute, sourceVertexIds))
    })

    const IndexArray = sourceVertexIds.length > 65535 ? Uint32Array : Uint16Array
    geometry.setIndex(new THREE.BufferAttribute(new IndexArray(targetIndices), 1))

    groups.forEach((group) => {
        geometry.addGroup(group.start, group.count, group.materialIndex)
    })

    if (!geometry.getAttribute('normal') && geometry.getAttribute('position')) {
        geometry.computeVertexNormals()
    }

    geometry.computeBoundingBox()
    const center = geometry.boundingBox?.getCenter(new THREE.Vector3()) || new THREE.Vector3()
    const dimensions = geometry.boundingBox?.getSize(new THREE.Vector3()) || new THREE.Vector3()

    geometry.translate(-center.x, -center.y, -center.z)
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()

    const materialIndices = [...new Set(groups.map((group) => group.materialIndex))]
    const shapeDescriptor = buildPartShapeDescriptor(
        geometry,
        dimensions,
        triangleIds.length
    )
    const signature = shapeDescriptor.signature

    geometry.userData = {
        ...(geometry.userData || {}),
        __modelPartSignature: signature,
    }

    return {
        geometry,
        center: center.toArray(),
        dimensions: {
            x: dimensions.x,
            y: dimensions.y,
            z: dimensions.z,
        },
        triangleCount: triangleIds.length,
        vertexCount: sourceVertexIds.length,
        materialIndices,
        signature,
        shapeProfile: shapeDescriptor.profile,
    }
}

const getAxisGap = (leftMin, leftMax, rightMin, rightMax) => {
    if (leftMax < rightMin) return rightMin - leftMax
    if (rightMax < leftMin) return leftMin - rightMax
    return 0
}

const getAxisOverlap = (leftMin, leftMax, rightMin, rightMax) => (
    Math.max(0, Math.min(leftMax, rightMax) - Math.max(leftMin, rightMin))
)

const getTriangleGroupRecord = ({
    id,
    triangleIds,
    sourceIndex,
    position,
    triangleMaterialIndices,
    pointSampleTolerance,
    boundaryTolerance = pointSampleTolerance,
    maximumSamplePoints = 384,
}) => {
    const min = new THREE.Vector3(Infinity, Infinity, Infinity)
    const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity)
    const vertexIds = new Set()
    const materialIndices = new Set()
    const uniquePoints = new Map()
    const boundaryEdgeRecords = new Map()
    const safeBoundaryTolerance = Math.max(0.000000001, Number(boundaryTolerance) || 0.000000001)
    const safePointTolerance = Math.max(0.000000001, Number(pointSampleTolerance) || 0.000000001)

    const getBoundaryVertexKey = (vertexId) => ([
        Math.round((Number(position.getX(vertexId)) || 0) / safeBoundaryTolerance),
        Math.round((Number(position.getY(vertexId)) || 0) / safeBoundaryTolerance),
        Math.round((Number(position.getZ(vertexId)) || 0) / safeBoundaryTolerance),
    ].join(':'))

    const addBoundaryEdge = (leftVertexId, rightVertexId) => {
        const leftKey = getBoundaryVertexKey(leftVertexId)
        const rightKey = getBoundaryVertexKey(rightVertexId)
        const edgeKey = leftKey < rightKey
            ? `${leftKey}|${rightKey}`
            : `${rightKey}|${leftKey}`
        const current = boundaryEdgeRecords.get(edgeKey)

        if (current) {
            current.count += 1
            return
        }

        boundaryEdgeRecords.set(edgeKey, {
            count: 1,
            leftVertexId,
            rightVertexId,
        })
    }

    triangleIds.forEach((triangleId) => {
        materialIndices.add(Math.max(0, Number(triangleMaterialIndices[triangleId]) || 0))

        const offset = triangleId * 3
        const triangleVertexIds = [
            Number(sourceIndex.getX(offset)),
            Number(sourceIndex.getX(offset + 1)),
            Number(sourceIndex.getX(offset + 2)),
        ]

        addBoundaryEdge(triangleVertexIds[0], triangleVertexIds[1])
        addBoundaryEdge(triangleVertexIds[1], triangleVertexIds[2])
        addBoundaryEdge(triangleVertexIds[2], triangleVertexIds[0])

        for (let corner = 0; corner < 3; corner += 1) {
            const vertexId = triangleVertexIds[corner]

            if (!vertexIds.has(vertexId)) {
                vertexIds.add(vertexId)

                const x = Number(position.getX(vertexId)) || 0
                const y = Number(position.getY(vertexId)) || 0
                const z = Number(position.getZ(vertexId)) || 0

                min.x = Math.min(min.x, x)
                min.y = Math.min(min.y, y)
                min.z = Math.min(min.z, z)
                max.x = Math.max(max.x, x)
                max.y = Math.max(max.y, y)
                max.z = Math.max(max.z, z)

                const pointKey = [
                    Math.round(x / safePointTolerance),
                    Math.round(y / safePointTolerance),
                    Math.round(z / safePointTolerance),
                ].join(':')

                if (!uniquePoints.has(pointKey)) {
                    uniquePoints.set(pointKey, [x, y, z])
                }
            }
        }
    })

    if (!vertexIds.size) {
        min.set(0, 0, 0)
        max.set(0, 0, 0)
    }

    const allPoints = [...uniquePoints.values()]
    const samplePoints = []
    const targetSampleCount = Math.min(
        Math.max(1, Number(maximumSamplePoints) || 384),
        allPoints.length
    )

    for (let sampleIndex = 0; sampleIndex < targetSampleCount; sampleIndex += 1) {
        const sourcePointIndex = targetSampleCount <= 1
            ? 0
            : Math.round((sampleIndex / (targetSampleCount - 1)) * (allPoints.length - 1))

        samplePoints.push(allPoints[sourcePointIndex] || [0, 0, 0])
    }

    let boundaryEdgeCount = 0
    let boundaryLength = 0
    const boundarySegments = []

    boundaryEdgeRecords.forEach((record) => {
        if (record.count !== 1) return

        boundaryEdgeCount += 1

        const start = new THREE.Vector3(
            Number(position.getX(record.leftVertexId)) || 0,
            Number(position.getY(record.leftVertexId)) || 0,
            Number(position.getZ(record.leftVertexId)) || 0
        )
        const end = new THREE.Vector3(
            Number(position.getX(record.rightVertexId)) || 0,
            Number(position.getY(record.rightVertexId)) || 0,
            Number(position.getZ(record.rightVertexId)) || 0
        )
        const length = start.distanceTo(end)

        if (!Number.isFinite(length) || length <= safeBoundaryTolerance * 0.1) return

        boundaryLength += length
        boundarySegments.push({
            start,
            end,
            length,
            min: new THREE.Vector3(
                Math.min(start.x, end.x),
                Math.min(start.y, end.y),
                Math.min(start.z, end.z)
            ),
            max: new THREE.Vector3(
                Math.max(start.x, end.x),
                Math.max(start.y, end.y),
                Math.max(start.z, end.z)
            ),
        })
    })

    const dimensions = max.clone().sub(min)
    const center = min.clone().add(max).multiplyScalar(0.5)

    return {
        id,
        triangleIds: [...triangleIds],
        triangleCount: triangleIds.length,
        vertexCount: vertexIds.size,
        materialIndices,
        boundaryEdgeCount,
        boundaryLength,
        boundarySegments,
        isOpenSurface: boundaryEdgeCount > 0,
        min,
        max,
        dimensions,
        center,
        samplePoints,
        pointGrid: null,
    }
}

const buildPointGrid = (points, cellSize) => {
    const grid = new Map()
    const safeCellSize = Math.max(0.000000001, Number(cellSize) || 0.000000001)

    ;(Array.isArray(points) ? points : []).forEach((point) => {
        const x = Number(point?.[0]) || 0
        const y = Number(point?.[1]) || 0
        const z = Number(point?.[2]) || 0
        const cellX = Math.floor(x / safeCellSize)
        const cellY = Math.floor(y / safeCellSize)
        const cellZ = Math.floor(z / safeCellSize)
        const key = `${cellX}:${cellY}:${cellZ}`

        if (!grid.has(key)) grid.set(key, [])
        grid.get(key).push([x, y, z])
    })

    return grid
}

const hasNearbyPoint = (point, targetGrid, tolerance) => {
    const safeTolerance = Math.max(0.000000001, Number(tolerance) || 0.000000001)
    const toleranceSquared = safeTolerance * safeTolerance
    const x = Number(point?.[0]) || 0
    const y = Number(point?.[1]) || 0
    const z = Number(point?.[2]) || 0
    const cellX = Math.floor(x / safeTolerance)
    const cellY = Math.floor(y / safeTolerance)
    const cellZ = Math.floor(z / safeTolerance)

    for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
        for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
            for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
                const candidates = targetGrid.get(
                    `${cellX + offsetX}:${cellY + offsetY}:${cellZ + offsetZ}`
                ) || []

                for (let index = 0; index < candidates.length; index += 1) {
                    const candidate = candidates[index]
                    const dx = x - candidate[0]
                    const dy = y - candidate[1]
                    const dz = z - candidate[2]

                    if ((dx * dx) + (dy * dy) + (dz * dz) <= toleranceSquared) {
                        return true
                    }
                }
            }
        }
    }

    return false
}

const measureNearPointRatio = (left, right, tolerance) => {
    const leftPoints = Array.isArray(left?.samplePoints) ? left.samplePoints : []
    const rightPoints = Array.isArray(right?.samplePoints) ? right.samplePoints : []

    if (!leftPoints.length || !rightPoints.length) return 0

    const source = leftPoints.length <= rightPoints.length ? left : right
    const target = source === left ? right : left
    const sourcePoints = source.samplePoints

    if (!target.pointGrid) {
        target.pointGrid = buildPointGrid(target.samplePoints, tolerance)
    }

    let nearbyCount = 0

    sourcePoints.forEach((point) => {
        if (hasNearbyPoint(point, target.pointGrid, tolerance)) nearbyCount += 1
    })

    return nearbyCount / Math.max(1, sourcePoints.length)
}

const materialSetsDiffer = (left, right) => {
    for (const materialIndex of left.materialIndices) {
        if (!right.materialIndices.has(materialIndex)) return true
    }

    for (const materialIndex of right.materialIndices) {
        if (!left.materialIndices.has(materialIndex)) return true
    }

    return false
}

const getBoundsCompatibility = (left, right, tolerance) => {
    const axes = ['x', 'y', 'z']
    const overlapRatios = {}
    const gaps = {}
    let compatibleAxisCount = 0

    for (let index = 0; index < axes.length; index += 1) {
        const axis = axes[index]
        const gap = getAxisGap(
            left.min[axis],
            left.max[axis],
            right.min[axis],
            right.max[axis]
        )

        gaps[axis] = gap

        if (gap > tolerance * 1.75) {
            return {
                compatible: false,
                compatibleAxisCount,
                overlapRatios,
                gaps,
            }
        }

        const smallerSpan = Math.max(
            0,
            Math.min(left.dimensions[axis], right.dimensions[axis])
        )
        let overlapRatio = 0

        if (smallerSpan <= tolerance * 1.5) {
            overlapRatio = gap <= tolerance ? 1 : 0
        } else {
            overlapRatio = getAxisOverlap(
                left.min[axis],
                left.max[axis],
                right.min[axis],
                right.max[axis]
            ) / smallerSpan
        }

        overlapRatios[axis] = overlapRatio
        if (overlapRatio >= 0.3) compatibleAxisCount += 1
    }

    return {
        compatible: compatibleAxisCount >= 2,
        compatibleAxisCount,
        overlapRatios,
        gaps,
    }
}

const pointToSegmentDistanceSquared = (point, segment) => {
    const ab = segment.end.clone().sub(segment.start)
    const abLengthSquared = ab.lengthSq()

    if (abLengthSquared <= Number.EPSILON) {
        return point.distanceToSquared(segment.start)
    }

    const t = Math.min(
        1,
        Math.max(
            0,
            point.clone().sub(segment.start).dot(ab) / abLengthSquared
        )
    )
    const closest = segment.start.clone().addScaledVector(ab, t)

    return point.distanceToSquared(closest)
}

const pointMayReachSegment = (point, segment, tolerance) => (
    point.x >= segment.min.x - tolerance &&
    point.x <= segment.max.x + tolerance &&
    point.y >= segment.min.y - tolerance &&
    point.y <= segment.max.y + tolerance &&
    point.z >= segment.min.z - tolerance &&
    point.z <= segment.max.z + tolerance
)

const measureMaterialBoundaryCoverage = (left, right, tolerance) => {
    const leftSegments = Array.isArray(left?.boundarySegments)
        ? left.boundarySegments
        : []
    const rightSegments = Array.isArray(right?.boundarySegments)
        ? right.boundarySegments
        : []

    if (!leftSegments.length || !rightSegments.length) {
        return {
            coverage: 0,
            matchedLength: 0,
            matchedSegmentCount: 0,
            sourceBoundaryLength: 0,
        }
    }

    const source = (left.boundaryLength || 0) <= (right.boundaryLength || 0)
        ? left
        : right
    const target = source === left ? right : left
    const sourceSegments = source.boundarySegments
    const targetSegments = target.boundarySegments
    const toleranceSquared = tolerance * tolerance
    let matchedLength = 0
    let matchedSegmentCount = 0

    sourceSegments.forEach((sourceSegment) => {
        const sampleCount = Math.max(
            3,
            Math.min(
                9,
                Math.ceil(sourceSegment.length / Math.max(tolerance * 6, 0.000000001))
            )
        )
        let matchedSamples = 0

        for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
            // Берём точки внутри ребра, а не его вершины. Так случайное
            // пересечение двух отдельных деталей одним углом не считается
            // общим материальным швом.
            const t = (sampleIndex + 0.5) / sampleCount
            const point = sourceSegment.start.clone().lerp(sourceSegment.end, t)
            let matched = false

            for (let targetIndex = 0; targetIndex < targetSegments.length; targetIndex += 1) {
                const targetSegment = targetSegments[targetIndex]

                if (!pointMayReachSegment(point, targetSegment, tolerance)) continue

                if (
                    pointToSegmentDistanceSquared(point, targetSegment) <=
                    toleranceSquared
                ) {
                    matched = true
                    break
                }
            }

            if (matched) matchedSamples += 1
        }

        const segmentCoverage = matchedSamples / sampleCount

        if (segmentCoverage >= 0.5) {
            matchedSegmentCount += 1
        }

        matchedLength += sourceSegment.length * segmentCoverage
    })

    const sourceBoundaryLength = Math.max(
        tolerance,
        Number(source.boundaryLength) || 0
    )

    return {
        coverage: matchedLength / sourceBoundaryLength,
        matchedLength,
        matchedSegmentCount,
        sourceBoundaryLength,
    }
}

const shouldMergeDifferentMaterialRecords = (left, right, tolerance) => {
    if (!materialSetsDiffer(left, right)) return false

    // Материал сам по себе никогда не является отдельной заготовкой. Но и
    // простое пересечение/заход одной детали в другую на 1 мм не означает,
    // что это одно тело. Склеиваем только открытые material-primitives,
    // у которых действительно совпадает большая часть открытого контура.
    if (!left.isOpenSurface || !right.isOpenSurface) return false

    const bounds = getBoundsCompatibility(left, right, tolerance * 1.5)
    if (!bounds.compatible) return false

    const boundary = measureMaterialBoundaryCoverage(left, right, tolerance)
    const minimumMatchedLength = Math.max(
        tolerance * 12,
        boundary.sourceBoundaryLength * 0.58
    )
    const minimumMatchedSegments = Math.max(
        2,
        Math.ceil(
            Math.min(
                left.boundarySegments?.length || 0,
                right.boundarySegments?.length || 0
            ) * 0.08
        )
    )

    return (
        boundary.coverage >= 0.68 &&
        boundary.matchedLength >= minimumMatchedLength &&
        boundary.matchedSegmentCount >= minimumMatchedSegments
    )
}

const getFragmentHostScore = (fragment, host, tolerance) => {
    const axes = ['x', 'y', 'z']
    const sortedAxes = axes
        .map((axis) => ({
            axis,
            size: Math.max(0, Number(fragment.dimensions?.[axis]) || 0),
        }))
        .sort((left, right) => right.size - left.size)
    const surfaceAxes = sortedAxes.slice(0, 2).map((item) => item.axis)
    const normalAxis = sortedAxes[2]?.axis || 'z'
    let coveredSurfaceAxes = 0
    let coverageScore = 0
    let maximumGap = 0

    axes.forEach((axis) => {
        const gap = getAxisGap(
            fragment.min[axis],
            fragment.max[axis],
            host.min[axis],
            host.max[axis]
        )

        maximumGap = Math.max(maximumGap, gap)
    })

    if (maximumGap > tolerance * 8) return null

    surfaceAxes.forEach((axis) => {
        const fragmentSpan = Math.max(
            tolerance,
            Number(fragment.dimensions?.[axis]) || 0
        )
        const overlapRatio = getAxisOverlap(
            fragment.min[axis],
            fragment.max[axis],
            host.min[axis],
            host.max[axis]
        ) / fragmentSpan

        coverageScore += Math.min(1, overlapRatio)
        if (overlapRatio >= 0.15) coveredSurfaceAxes += 1
    })

    const normalGap = getAxisGap(
        fragment.min[normalAxis],
        fragment.max[normalAxis],
        host.min[normalAxis],
        host.max[normalAxis]
    )

    if (coveredSurfaceAxes < 2 || normalGap > tolerance * 8) return null

    const nearPointRatio = measureNearPointRatio(fragment, host, tolerance * 2)
    const centerDistance = fragment.center.distanceTo(host.center)
    const hostDiagonal = Math.max(tolerance, host.dimensions.length())
    const sameMaterial = !materialSetsDiffer(fragment, host)

    return (
        (sameMaterial ? 1000 : 0) +
        nearPointRatio * 500 +
        coverageScore * 100 -
        (maximumGap / tolerance) * 4 -
        (centerDistance / hostDiagonal)
    )
}

const isLikelyTinySurfaceFragment = ({
    record,
    overallDiagonal,
    repairTolerance,
    maximumFragmentTriangles,
}) => {
    const triangleCount = Math.max(0, Number(record?.triangleCount) || 0)
    const hardTriangleLimit = Math.max(4, Number(maximumFragmentTriangles) || 12)

    if (triangleCount <= 4) return true
    if (triangleCount > hardTriangleLimit) return false

    const dimensions = [
        Math.max(0, Number(record?.dimensions?.x) || 0),
        Math.max(0, Number(record?.dimensions?.y) || 0),
        Math.max(0, Number(record?.dimensions?.z) || 0),
    ].sort((left, right) => left - right)
    const diagonal = Math.max(0.000001, Number(overallDiagonal) || 0.000001)
    const tolerance = Math.max(0.0000001, Number(repairTolerance) || 0.0000001)
    const thinLimit = Math.max(
        tolerance * 5,
        Math.min(0.004, diagonal * 0.0015)
    )
    const narrowLimit = Math.max(
        tolerance * 10,
        Math.min(0.012, diagonal * 0.004)
    )

    // Типичный мусорный фрагмент после Boolean/материального шва — это
    // длинная узкая полоска: например 3×80×1 мм. Настоящие профили ограды
    // 30×30 мм и каменные заготовки под этот критерий не попадают.
    return dimensions[0] <= thinLimit && dimensions[1] <= narrowLimit
}

const attachTinyTriangleGroups = ({
    triangleGroups,
    sourceIndex,
    position,
    triangleMaterialIndices,
    repairTolerance,
    safeTolerance,
    maximumFragmentTriangles = 12,
    overallDiagonal = 1,
}) => {
    const records = triangleGroups.map((triangleIds, id) => (
        getTriangleGroupRecord({
            id,
            triangleIds,
            sourceIndex,
            position,
            triangleMaterialIndices,
            pointSampleTolerance: repairTolerance * 0.25,
            boundaryTolerance: safeTolerance,
            maximumSamplePoints: 96,
        })
    ))
    const fragmentLimit = Math.max(4, Number(maximumFragmentTriangles) || 12)
    const fragmentIds = new Set(records
        .filter((record) => isLikelyTinySurfaceFragment({
            record,
            overallDiagonal,
            repairTolerance,
            maximumFragmentTriangles: fragmentLimit,
        }))
        .map((record) => record.id))
    const fragments = records.filter((record) => fragmentIds.has(record.id))
    const hosts = records.filter((record) => !fragmentIds.has(record.id))
    const attachedTo = new Map()
    const discarded = new Set()

    fragments.forEach((fragment) => {
        let bestHost = null
        let bestScore = -Infinity

        hosts.forEach((host) => {
            const score = getFragmentHostScore(fragment, host, repairTolerance)

            if (score === null || score <= bestScore) return

            bestHost = host
            bestScore = score
        })

        if (bestHost) {
            attachedTo.set(fragment.id, bestHost.id)
        } else {
            // Малый открытый фрагмент или узкая полоска до 12 треугольников
            // не является самостоятельной заготовкой. Это шов материала,
            // фаска после Boolean либо служебный кусок геометрии.
            discarded.add(fragment.id)
        }
    })

    const mergedById = new Map()

    records.forEach((record) => {
        if (attachedTo.has(record.id) || discarded.has(record.id)) return
        mergedById.set(record.id, [...record.triangleIds])
    })

    records.forEach((record) => {
        const hostId = attachedTo.get(record.id)
        if (hostId === undefined) return

        const target = mergedById.get(hostId)
        if (!target) return

        target.push(...record.triangleIds)
    })

    return {
        triangleGroups: [...mergedById.values()],
        attachedFragmentCount: attachedTo.size,
        discardedFragmentCount: discarded.size,
    }
}

const mergeDifferentMaterialTriangleGroups = ({
    triangleGroups,
    sourceIndex,
    position,
    triangleMaterialIndices,
    repairTolerance,
    safeTolerance,
}) => {
    if (!Array.isArray(triangleGroups) || triangleGroups.length < 2) {
        return {
            triangleGroups: Array.isArray(triangleGroups) ? triangleGroups : [],
            mergedPairCount: 0,
        }
    }

    const records = triangleGroups.map((triangleIds, id) => (
        getTriangleGroupRecord({
            id,
            triangleIds,
            sourceIndex,
            position,
            triangleMaterialIndices,
            pointSampleTolerance: repairTolerance * 0.25,
            boundaryTolerance: safeTolerance,
            maximumSamplePoints: 384,
        })
    ))
    const unionFind = createUnionFind(records.length)
    let mergedPairCount = 0

    for (let leftIndex = 0; leftIndex < records.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < records.length; rightIndex += 1) {
            const left = records[leftIndex]
            const right = records[rightIndex]

            if (!shouldMergeDifferentMaterialRecords(left, right, repairTolerance)) continue

            if (unionFind.union(leftIndex, rightIndex)) {
                mergedPairCount += 1
            }
        }
    }

    const trianglesByRoot = new Map()

    records.forEach((record, index) => {
        const root = unionFind.find(index)

        if (!trianglesByRoot.has(root)) trianglesByRoot.set(root, [])
        trianglesByRoot.get(root).push(...record.triangleIds)
    })

    return {
        triangleGroups: [...trianglesByRoot.values()],
        mergedPairCount,
    }
}

const buildRawMaterialTriangleGroups = ({
    triangleCount,
    sourceIndex,
    position,
    triangleMaterialIndices,
    safeTolerance,
}) => {
    const vertexKeys = new Array(position.count)
    const edgeOwners = new Map()
    const unionFind = createUnionFind(triangleCount)

    const getVertexKey = (vertexIndex) => {
        if (vertexKeys[vertexIndex]) return vertexKeys[vertexIndex]

        const key = [
            Math.round((Number(position.getX(vertexIndex)) || 0) / safeTolerance),
            Math.round((Number(position.getY(vertexIndex)) || 0) / safeTolerance),
            Math.round((Number(position.getZ(vertexIndex)) || 0) / safeTolerance),
        ].join(':')

        vertexKeys[vertexIndex] = key
        return key
    }

    const addEdgeOwner = (
        leftKey,
        rightKey,
        leftVertexIndex,
        rightVertexIndex,
        triangleId,
        materialIndex
    ) => {
        const edgeKey = leftKey < rightKey
            ? `${leftKey}|${rightKey}`
            : `${rightKey}|${leftKey}`
        const indexedEdgeKey = leftVertexIndex < rightVertexIndex
            ? `${leftVertexIndex}|${rightVertexIndex}`
            : `${rightVertexIndex}|${leftVertexIndex}`

        if (!edgeOwners.has(edgeKey)) edgeOwners.set(edgeKey, [])
        edgeOwners.get(edgeKey).push({
            triangleId,
            materialIndex,
            indexedEdgeKey,
        })
    }

    for (let triangleId = 0; triangleId < triangleCount; triangleId += 1) {
        const offset = triangleId * 3
        const a = Number(sourceIndex.getX(offset))
        const b = Number(sourceIndex.getX(offset + 1))
        const c = Number(sourceIndex.getX(offset + 2))
        const keyA = getVertexKey(a)
        const keyB = getVertexKey(b)
        const keyC = getVertexKey(c)
        const materialIndex = Math.max(0, Number(triangleMaterialIndices[triangleId]) || 0)

        addEdgeOwner(keyA, keyB, a, b, triangleId, materialIndex)
        addEdgeOwner(keyB, keyC, b, c, triangleId, materialIndex)
        addEdgeOwner(keyC, keyA, c, a, triangleId, materialIndex)
    }

    let contactEdgeCount = 0
    let indexedContactPairCount = 0
    let crossMaterialEdgeCount = 0

    edgeOwners.forEach((owners) => {
        const uniqueByTriangle = new Map()

        owners.forEach((owner) => {
            if (!uniqueByTriangle.has(owner.triangleId)) {
                uniqueByTriangle.set(owner.triangleId, owner)
            }
        })

        const uniqueOwners = [...uniqueByTriangle.values()]
        const ownersByMaterial = new Map()

        uniqueOwners.forEach((owner) => {
            if (!ownersByMaterial.has(owner.materialIndex)) {
                ownersByMaterial.set(owner.materialIndex, [])
            }

            ownersByMaterial.get(owner.materialIndex).push(owner)
        })

        if (ownersByMaterial.size > 1) crossMaterialEdgeCount += 1

        ownersByMaterial.forEach((materialOwners) => {
            if (materialOwners.length === 2) {
                unionFind.union(
                    materialOwners[0].triangleId,
                    materialOwners[1].triangleId
                )
                return
            }

            if (materialOwners.length <= 1) return

            // Несколько граней одного материала могут совпасть, когда разные
            // заготовки в собранном виде касаются друг друга. Соединять всё
            // геометрическое ребро нельзя — иначе четыре отдельных блока снова
            // превратятся в один. Оставляем только настоящую индексную связь.
            contactEdgeCount += 1

            const ownersByIndexedEdge = new Map()

            materialOwners.forEach((owner) => {
                if (!ownersByIndexedEdge.has(owner.indexedEdgeKey)) {
                    ownersByIndexedEdge.set(owner.indexedEdgeKey, [])
                }

                ownersByIndexedEdge.get(owner.indexedEdgeKey).push(owner.triangleId)
            })

            ownersByIndexedEdge.forEach((triangleIds) => {
                if (triangleIds.length < 2) return

                const firstTriangleId = triangleIds[0]

                for (let index = 1; index < triangleIds.length; index += 1) {
                    if (unionFind.union(firstTriangleId, triangleIds[index])) {
                        indexedContactPairCount += 1
                    }
                }
            })
        })
    })

    const trianglesByRoot = new Map()

    for (let triangleId = 0; triangleId < triangleCount; triangleId += 1) {
        const root = unionFind.find(triangleId)

        if (!trianglesByRoot.has(root)) trianglesByRoot.set(root, [])
        trianglesByRoot.get(root).push(triangleId)
    }

    return {
        triangleGroups: [...trianglesByRoot.values()],
        contactEdgeCount,
        indexedContactPairCount,
        crossMaterialEdgeCount,
    }
}

/**
 * Разбирает модель на физические заготовки, а не на материалы.
 *
 * GLB хранит один Blender-объект с двумя или тремя материалами отдельными
 * primitives. После merge у этих primitives разные индексы вершин, поэтому
 * обычный поиск connected components ошибочно превращает полировку, низ и
 * боковые поверхности в самостоятельные детали.
 *
 * Здесь два простых этапа:
 * 1. Внутри каждого материала восстанавливаем только настоящую топологию.
 * 2. Компоненты разных материалов объединяем только по совпавшему открытому
 *    контуру material-primitives. Объёмное пересечение и заход одной детали
 *    в другую не являются основанием для объединения.
 *
 * Компоненты одного материала по близости никогда не склеиваются. Поэтому
 * отдельные нижние блоки, которые лишь соприкасаются в собранном виде,
 * сохраняются отдельными заготовками.
 */
export const extractDisconnectedModelParts = (sourceGeometry, {
    minimumTriangleCount = 2,
    tolerance = null,
    materialRepairTolerance = null,
} = {}) => {
    if (!sourceGeometry?.isBufferGeometry) {
        throw new Error('Для разбора модели не найдена BufferGeometry')
    }

    const geometry = stripUnusedGltfAttributes(sourceGeometry.clone())
    ensureIndexedGeometry(geometry)

    const position = geometry.getAttribute('position')
    const index = geometry.index
    const triangleCount = Math.floor(Number(index?.count || 0) / 3)

    if (!position || !index || triangleCount < 1) {
        geometry.dispose()
        throw new Error('В модели не найдены треугольники для разбора')
    }

    geometry.computeBoundingBox()
    const overallCenter = geometry.boundingBox?.getCenter(new THREE.Vector3()) || new THREE.Vector3()
    const overallDimensions = geometry.boundingBox?.getSize(new THREE.Vector3()) || new THREE.Vector3()
    const diagonal = Math.max(0.000001, overallDimensions.length())
    const safeTolerance = Math.max(
        0.0000001,
        tolerance !== null && tolerance !== undefined && Number.isFinite(Number(tolerance))
            ? Number(tolerance)
            : diagonal * 0.000002
    )
    const repairTolerance = Math.max(
        safeTolerance * 32,
        materialRepairTolerance !== null && materialRepairTolerance !== undefined && Number.isFinite(Number(materialRepairTolerance))
            ? Number(materialRepairTolerance)
            : Math.min(diagonal * 0.00006, 0.0005)
    )
    const triangleMaterialIndices = getTriangleMaterialIndices(geometry, triangleCount)
    const raw = buildRawMaterialTriangleGroups({
        triangleCount,
        sourceIndex: index,
        position,
        triangleMaterialIndices,
        safeTolerance,
    })
    const minimumTriangles = Math.max(1, Number(minimumTriangleCount) || 1)
    const rawTriangleGroups = raw.triangleGroups.filter((triangleIds) => (
        triangleIds.length >= minimumTriangles
    ))
    const fragmentRepair = attachTinyTriangleGroups({
        triangleGroups: rawTriangleGroups,
        sourceIndex: index,
        position,
        triangleMaterialIndices,
        repairTolerance,
        safeTolerance,
        maximumFragmentTriangles: 12,
        overallDiagonal: diagonal,
    })
    const materialRepair = mergeDifferentMaterialTriangleGroups({
        triangleGroups: fragmentRepair.triangleGroups,
        sourceIndex: index,
        position,
        triangleMaterialIndices,
        repairTolerance,
        safeTolerance,
    })
    const parts = materialRepair.triangleGroups
        .filter((triangleIds) => triangleIds.length >= minimumTriangles)
        .map((triangleIds) => buildDisconnectedPartGeometry({
            sourceGeometry: geometry,
            sourceIndex: index,
            triangleIds,
            triangleMaterialIndices,
        }))

    parts.sort((left, right) => {
        const leftCenter = left.center || [0, 0, 0]
        const rightCenter = right.center || [0, 0, 0]

        return (
            (leftCenter[2] - rightCenter[2]) ||
            (leftCenter[0] - rightCenter[0]) ||
            (leftCenter[1] - rightCenter[1]) ||
            (right.triangleCount - left.triangleCount)
        )
    })

    parts.forEach((part, indexValue) => {
        part.index = indexValue
    })

    const orderedTypeGroups = assignStablePartTypeOrder(parts)

    parts.forEach((part, indexValue) => {
        part.key = `part-${indexValue + 1}-${hashText(part.signature)}`
        part.geometry.userData.__modelPartKey = part.key
        delete part.shapeProfile
    })

    geometry.dispose()

    return {
        parts,
        analysis: {
            version: MODEL_PARTS_ANALYSIS_VERSION,
            partCount: parts.length,
            triangleCount,
            tolerance: safeTolerance,
            materialRepairTolerance: repairTolerance,
            overallCenter: overallCenter.toArray(),
            overallDimensions: {
                x: overallDimensions.x,
                y: overallDimensions.y,
                z: overallDimensions.z,
            },
            signatures: orderedTypeGroups.length,
            contactEdgeCount: raw.contactEdgeCount,
            indexedContactPairCount: raw.indexedContactPairCount,
            crossMaterialEdgeCount: raw.crossMaterialEdgeCount,
            attachedSurfaceFragmentCount: fragmentRepair.attachedFragmentCount,
            discardedSurfaceFragmentCount: fragmentRepair.discardedFragmentCount,
            mergedMaterialSeamCount: materialRepair.mergedPairCount,
            geometricMaterialSeamCount: materialRepair.mergedPairCount,
            rawPartCountBeforeSurfaceRepair: rawTriangleGroups.length,
            partCountAfterFragmentRepair: fragmentRepair.triangleGroups.length,
            partCountAfterMaterialSeamRepair: materialRepair.triangleGroups.length,
        },
    }
}
